import { useState, useEffect } from "react";
import OnboardingFlow from "@/components/OnboardingFlow";
import TemplatePreviewModal from "@/components/TemplatePreviewModal";
import ComplianceDashboard from "@/components/ComplianceDashboard";
import StandardsNotificationCenter from "@/components/StandardsNotificationCenter";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FileText, Upload, Clock, CheckCircle2, XCircle, LogOut, BarChart3, FolderOpen, Plus, Award, Bell, Shield, TrendingUp, Key, User, Lock, BookOpen, FileArchive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, logout }) {
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem("standari_token");
      const response = await axios.get(`${API}/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.show_onboarding && !response.data.completed) {
        setOnboardingComplete(false);
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error("Failed to check onboarding status:", error);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setOnboardingComplete(true);
  };

  const handleTemplatePreview = (template) => {
    setSelectedTemplate(template);
    setShowTemplatePreview(true);
  };

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("standari_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [docsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/documents`, { headers }),
        axios.get(`${API}/analytics/dashboard`, { headers })
      ]);
      
      setDocuments(docsRes.data.documents);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case "processing": return <Clock className="h-5 w-5 text-yellow-600" />;
      case "failed": return <XCircle className="h-5 w-5 text-red-600" />;
      default: return <Upload className="h-5 w-5 text-blue-600" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const handleDocumentClick = (doc) => {
    if (doc.status === "completed" && doc.processed_content_id) {
      navigate(`/editor/${doc.processed_content_id}`);
    } else if (doc.status === "uploaded") {
      toast.info("Please process this document first");
    }
  };

  return (
    <div className="min-h-screen gradient-bg" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <span className="text-2xl font-heading font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">Standari</span>
            </div>
            <div className="flex items-center space-x-4">
              {/* Standards Notification Center */}
              <StandardsNotificationCenter />
              
              <span className="text-slate-600" data-testid="user-email">Welcome, {user.username}</span>
              <Button variant="outline" onClick={() => navigate("/audit-bundles")} data-testid="audit-bundles-nav-btn" title="One-click audit packages" className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 hover:from-blue-100 hover:to-purple-100">
                <FileArchive className="h-4 w-4 mr-2 text-blue-600" />
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-semibold">Audit Bundles</span>
              </Button>
              <Button variant="outline" onClick={() => navigate("/playbooks")} data-testid="playbooks-nav-btn" title="Guided workflows for compliance">
                <BookOpen className="h-4 w-4 mr-2" />
                Playbooks
              </Button>
              <Button variant="outline" onClick={() => navigate("/compliance")} data-testid="compliance-nav-btn" title="Search 100+ standards · Match docs instantly">
                <Award className="h-4 w-4 mr-2" />
                Standards
              </Button>
              <Button variant="outline" onClick={() => navigate("/analytics")} data-testid="analytics-nav-btn">
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <User className="h-4 w-4 mr-2" />
                    Account
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/account/security")}>
                    <Lock className="h-4 w-4 mr-2" />
                    Security Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account/api-keys")}>
                    <Key className="h-4 w-4 mr-2" />
                    API Keys
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/admin/security")}>
                        <Shield className="h-4 w-4 mr-2" />
                        Admin Security Dashboard
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Legal Disclaimer - Lawsuit Protection */}
        <DisclaimerBanner />

        {/* Compliance Dashboard - PSYCHOLOGY: Show success immediately */}
        <div className="mb-8">
          <ComplianceDashboard userId={user.id} />
        </div>

        {/* How It Works - Reduce Anxiety */}
        <Card className="mb-8 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-2 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  New to Standari? See How Documentation Transformation Works
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Watch your document move through our 6-stage pipeline. No black boxes. No uncertainty.
                  See exactly what happens from upload to compliant output.
                </p>
                <div className="flex space-x-3">
                  <Button 
                    onClick={() => navigate('/how-it-works')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    How It Works
                  </Button>
                  <Button 
                    onClick={() => navigate('/demo')}
                    variant="outline"
                  >
                    See Real Example (Defense)
                  </Button>
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>2 minute walkthrough</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Audit Bundles CTA - PRIORITY FEATURE */}
        <Card className="mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 border-0 shadow-xl">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1 text-white">
                <div className="flex items-center mb-3">
                  <FileArchive className="h-7 w-7 mr-2" />
                  <h3 className="text-2xl font-bold">
                    ⚡ One-Click Audit Bundles
                  </h3>
                </div>
                <p className="text-blue-100 text-base mb-4 leading-relaxed">
                  Stop scrambling when the inspector arrives. Generate complete, audit-ready documentation packages in seconds. 
                  OSHA, FDA, ISO, FAA—we've got you covered.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">✓ All Docs</div>
                  <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">✓ Compliance Reports</div>
                  <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">✓ Audit Trails</div>
                  <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">✓ Standards History</div>
                </div>
                <Button 
                  onClick={() => navigate('/audit-bundles')}
                  className="bg-white text-blue-600 hover:bg-blue-50 font-semibold shadow-lg"
                  size="lg"
                >
                  <FileArchive className="h-5 w-5 mr-2" />
                  Generate Your First Bundle
                </Button>
              </div>
              <div className="flex-shrink-0 hidden md:block">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border-2 border-white/20">
                  <FileArchive className="h-20 w-20 mx-auto mb-3 text-white" />
                  <p className="text-white text-lg font-bold">5 Bundles</p>
                  <p className="text-blue-100 text-sm">Ready to Generate</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Playbook CTA */}
        <Card className="mb-8 bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 border-2 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <BookOpen className="h-6 w-6 text-purple-600 mr-2" />
                  <h3 className="text-xl font-bold text-gray-900">
                    Guided Workflow Playbooks
                  </h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Turn complex compliance standards into step-by-step workflows anyone can follow.
                  Eliminate expert bottlenecks with guided processes for S1000D, FHIR, SOPs, and more.
                </p>
                <div className="flex space-x-3">
                  <Button 
                    onClick={() => navigate('/playbooks')}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Browse Playbooks
                  </Button>
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="flex flex-col gap-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span>3 industry playbooks</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span>Multi-role workflows</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span>Standards-driven</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Psychological Trust Signals */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <Shield className="h-8 w-8 text-green-600" />
            <div>
              <p className="font-semibold text-green-900">Enterprise Security</p>
              <p className="text-sm text-green-700">SOC 2 Ready · Encrypted</p>
            </div>
          </div>
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
            <Bell className="h-8 w-8 text-blue-600" />
            <div>
              <p className="font-semibold text-blue-900">Auto-Updating Standards</p>
              <p className="text-sm text-blue-700">15+ sources · Real-time alerts</p>
            </div>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-purple-600" />
            <div>
              <p className="font-semibold text-purple-900">Proven Results</p>
              <p className="text-sm text-purple-700">$168K avg. savings/year</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="doc-card" data-testid="stat-total-docs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Total Documents</CardTitle>
                <FolderOpen className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-heading">{analytics.total_documents}</div>
              </CardContent>
            </Card>
            <Card className="doc-card" data-testid="stat-processed-docs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Processed</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-heading">{analytics.processed_documents}</div>
              </CardContent>
            </Card>
            <Card className="doc-card" data-testid="stat-credits">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Usage Credits</CardTitle>
                <BarChart3 className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-heading">{analytics.usage_credits}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Button */}
        <div className="mb-8">
          <Button onClick={() => navigate("/process")} size="lg" className="btn-primary" data-testid="upload-new-doc-btn">
            <Plus className="h-5 w-5 mr-2" />
            Upload New Document
          </Button>
        </div>

        {/* Documents List */}
        <Card className="doc-card" data-testid="documents-list">
          <CardHeader>
            <CardTitle className="font-heading">Your Documents</CardTitle>
            <CardDescription>Manage and process your uploaded documents</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-600">Loading documents...</div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <Upload className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">No documents yet. Upload your first document to get started!</p>
                <Button onClick={() => navigate("/process")} data-testid="upload-first-doc-btn">
                  Upload Document
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    onClick={() => handleDocumentClick(doc)}
                    className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50/50 transition-all cursor-pointer"
                    data-testid={`document-item-${doc.id}`}
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FileText className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate" data-testid={`doc-filename-${doc.id}`}>{doc.filename}</h3>
                        <p className="text-sm text-slate-500">Uploaded {formatDate(doc.upload_date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(doc.status)}
                        <span className={`status-badge status-${doc.status}`} data-testid={`doc-status-${doc.id}`}>{doc.status}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}